/**
 * Personalised campaigns — list, create, duplicate, delete.
 *
 * A personalised campaign sends every recipient their own AI-generated product
 * image (the base product photo, redesigned in their company's brand). This
 * screen only holds the brief; generating, reviewing and sending all happen on
 * the review screen, which is where a freshly created campaign lands.
 */
define('mailingPersonalisedView',[
  'module',
  'app',
  'underscore',
  'jquery',
  'marionette',
  'mailingShell',
  'mailingImageLibrary',
  'mailingProofsheetProduct',
  'entities/mailing'
], function (
  module,
  App,
  _,
  $,
  Marionette,
  Shell,
  ImageLibrary,
  ProofsheetProduct,
  MailingApi
) {
  'use strict';

  var OCCASIONS = [
    'New Year', 'Valentine\'s Day', 'Easter', 'Mother\'s Day', 'Father\'s Day',
    'Summer', 'Back to School', 'Halloween', 'Black Friday', 'Christmas',
    'Company Anniversary', 'Product Launch', 'Trade Show', 'Thank You',
    'Welcome', 'Birthday', 'Other'
  ];

  var DESIGN_LANGUAGES = ['EN', 'FR', 'DE', 'NL', 'IT', 'ES', 'PT'];

  var STATUS_CLASS = {
    DRAFT: 'draft',
    GENERATING: 'scheduled',
    REVIEW: 'review',
    SENDING: 'sending',
    DONE: 'sent'
  };

  var PersonalisedView = Marionette.ItemView.extend({

    className: 'mailing-page mailing-personalised',

    template: _.template(
      '<%= nav %>' +
      '<div class="mlg-body"><div class="mlg-loading">Loading personalised campaigns…</div></div>' +
      '<div class="mlg-modal" style="display:none"></div>'
    ),

    ui: {
      body: '.mlg-body',
      modal: '.mlg-modal'
    },

    events: {
      'click .mlg-new-personalised': 'onNew',
      'click .mlg-open': 'onOpen',
      'click .mlg-duplicate': 'onDuplicate',
      'click .mlg-delete': 'onDelete',
      'blur .f-base-image': 'onBaseImageBlur',
      'click .mlg-choose-base-image': 'onChooseBaseImage',
      'click .mlg-choose-proofsheet': 'onChooseProofsheet',
      'click .mlg-clear-proofsheet': 'onClearProofsheet',
      'click .mlg-modal-cancel': 'closeModal',
      'click .mlg-modal-save': 'onCreate'
    },

    serializeData: function () {
      return {
        nav: Shell.navHtml('personalised',
          '<button type="button" class="mlg-btn primary mlg-new-personalised">New personalised campaign</button>')
      };
    },

    onShow: function () {
      this.load();
    },

    load: function () {
      var self = this;
      $.when(MailingApi.personalisedCampaigns(), MailingApi.templates(), MailingApi.segments())
        .done(function (campaigns, templates, segments) {
          self.templates = templates || [];
          self.segments = segments || [];
          self.renderList(campaigns || []);
        })
        .fail(function (xhr) {
          self.ui.body.html('<div class="mlg-error">' +
            _.escape(MailingApi.errorMessage(xhr)) + '</div>');
        });
    },

    renderList: function (campaigns) {
      if (!campaigns.length) {
        this.ui.body.html(Shell.emptyHtml('No personalised campaign yet. Create one to generate a ' +
          'custom product image for every recipient of a segment.'));
        return;
      }
      var rows = _.map(campaigns, function (campaign) {
        var status = campaign.status || 'DRAFT';
        // "sentItems" counts recipients handed to the outbox; what actually left
        // is the send log's SENT count, and what still waits is its QUEUED count.
        var progress = campaign.totalItems ?
            Shell.formatNumber(campaign.doneItems) + ' / ' + Shell.formatNumber(campaign.totalItems) +
              ' generated · ' + Shell.formatNumber(campaign.statQueued || 0) + ' in outbox · ' +
              Shell.formatNumber(campaign.statSent || 0) + ' sent' +
              (campaign.proofsheetCount ? ' · ' + Shell.formatNumber(campaign.proofsheetCount) + ' offer(s)' : '') : '—';

        return '<tr data-secid="' + campaign.secId + '">' +
          '<td class="c-name">' +
          '  <a class="mlg-open" href="#mailing/personalised/' + campaign.secId + '">' +
               _.escape(campaign.name || '(untitled)') + '</a>' +
          '  <div class="c-sub">' + _.escape(_.compact([campaign.occasion, campaign.productType]).join(' · ')) + '</div>' +
          '</td>' +
          '<td>' + _.escape(campaign.templateName || '—') + '</td>' +
          '<td>' + _.escape(campaign.segmentName || '—') + '</td>' +
          '<td><span class="mlg-chip st-' + (STATUS_CLASS[status] || 'draft') + '">' +
              Shell.prettyCase(status) + '</span></td>' +
          '<td class="c-when">' + _.escape(progress) + '</td>' +
          '<td>' + Shell.statsPillsHtml(campaign) + '</td>' +
          '<td class="c-actions">' +
          '  <a class="mlg-btn small primary mlg-open" href="#mailing/personalised/' + campaign.secId + '">' +
               (status === 'DRAFT' ? 'Set up' : 'Open') + '</a>' +
          '  <button type="button" class="mlg-btn small mlg-duplicate" title="Copy as a new draft">Duplicate</button>' +
          '  <button type="button" class="mlg-btn small danger mlg-delete">Delete</button>' +
          '</td>' +
          '</tr>';
      }).join('');

      this.ui.body.html(
        '<table class="mlg-table">' +
        '<thead><tr>' +
        '<th>Campaign</th><th>Template</th><th>Segment</th><th>Status</th><th>Progress</th>' +
        '<th>Performance</th><th></th>' +
        '</tr></thead>' +
        '<tbody>' + rows + '</tbody></table>');
    },

    //----------Create-------------------------------------------------------//

    onNew: function () {
      this.newProofsheet = null;
      var options = function (items, labelOf) {
        return '<option value=""></option>' + _.map(items, function (item) {
          return '<option value="' + item.secId + '">' + _.escape(labelOf(item)) + '</option>';
        }).join('');
      };
      var plain = function (values, selected) {
        return _.map(values, function (value) {
          return '<option' + (value === selected ? ' selected' : '') + '>' + _.escape(value) + '</option>';
        }).join('');
      };

      this.ui.modal.html(
        '<div class="mlg-modal-card wide">' +
        '<h3>New personalised campaign</h3>' +
        '<label>Name<input type="text" class="f-name" placeholder="e.g. Christmas mugs 2026"></label>' +
        '<div class="mlg-row">' +
        '  <label>Template<select class="f-template">' +
             options(this.templates, function (t) { return t.name || ''; }) + '</select></label>' +
        '  <label>Segment<select class="f-segment">' +
             options(this.segments, function (s) {
               return (s.name || '') + ' (' + Shell.formatNumber(s.subscriberCount) + ')';
             }) + '</select></label>' +
        '</div>' +
        '<div class="mlg-hint">In the template, put the merge field <code>{{personalised_image}}</code> where the ' +
          'image goes — or simply use the base product image below: its URL is swapped for each recipient\'s.</div>' +
        '<div class="mlg-row">' +
        '  <label class="grow">Base product image<input type="text" class="f-base-image" ' +
             'placeholder="https://…/product.png"></label>' +
        '  <button type="button" class="mlg-btn mlg-choose-base-image">Upload / choose…</button>' +
        '</div>' +
        '<div class="mlg-hint">The photo the AI redesigns. Shape, perspective and background are kept; only ' +
          'the printable surface changes.</div>' +
        '<div class="mlg-row">' +
        '  <label>Product type<input type="text" class="f-product-type" ' +
             'placeholder="e.g. ceramic mug, tote bag, notebook"></label>' +
        '  <label>Occasion<select class="f-occasion"><option value=""></option>' +
             plain(OCCASIONS) + '</select></label>' +
        '</div>' +
        '<label>Occasion details <span class="mlg-optional">optional — extra context for the AI</span>' +
          '<textarea class="f-occasion-details" rows="2" ' +
          'placeholder="e.g. Focus on winter cosiness, use snowflake motifs"></textarea></label>' +
        '<div class="mlg-row">' +
        '  <label class="grow">Mandatory text <span class="mlg-optional">optional — must appear on the product</span>' +
             '<input type="text" class="f-mandatory-text" placeholder="e.g. Happy Holidays 2026"></label>' +
        '  <label class="narrow">Text language<select class="f-design-language">' +
             plain(DESIGN_LANGUAGES, 'EN') + '</select></label>' +
        '</div>' +
        '<details class="mlg-advanced">' +
        '  <summary>Image prompt <span class="mlg-optional">advanced — the built-in one unless you change it</span></summary>' +
        Shell.imagePromptHtml() +
        '</details>' +
        '<label>Email subject<input type="text" class="f-subject"></label>' +
        '<div class="mlg-row">' +
        '  <label>From name<input type="text" class="f-from-name"></label>' +
        '  <label>From email<input type="text" class="f-from-email"></label>' +
        '</div>' +
        '<div class="mlg-hint">Leave the sender blank to use the mailing settings (per-language sender first, ' +
          'then the SMTP identity).</div>' +
        '<h4 class="mlg-subhead">Proofsheet button <span class="mlg-optional">optional — PIM products only</span></h4>' +
        '<div class="mlg-row mlg-proofsheet-pick">' +
        '  <div class="grow f-proofsheet-summary">No product: the email has no proofsheet button.</div>' +
        '  <button type="button" class="mlg-btn mlg-choose-proofsheet">Choose PIM product…</button>' +
        '  <button type="button" class="mlg-btn danger mlg-clear-proofsheet" style="display:none">Remove</button>' +
        '</div>' +
        '<div class="mlg-hint">A recipient who clicks a template button linked to <code>{{proofsheet_url}}</code> ' +
          '(and confirms) gets an offer with this configuration, their personalised image as its proofsheet. ' +
          'Can also be set or changed later, on the campaign page.</div>' +
        '<h4 class="mlg-subhead">Sending schedule</h4>' +
        '<label>Start sending on <span class="mlg-optional">optional — otherwise as soon as you send</span>' +
          '<input type="datetime-local" class="f-start-at"></label>' +
        Shell.sendWindowHtml(null) +
        '<div class="mlg-modal-actions">' +
        '  <button type="button" class="mlg-btn mlg-modal-cancel">Cancel</button>' +
        '  <button type="button" class="mlg-btn primary mlg-modal-save">Create</button>' +
        '</div>' +
        '</div>').show();

      var $modal = this.ui.modal;
      MailingApi.defaultPersonalisedImagePrompt().done(function (result) {
        Shell.bindImagePrompt($modal, null, (result || {}).prompt);
      }).fail(function () {
        Shell.bindImagePrompt($modal, null, '');
      });
    },

    onChooseBaseImage: function () {
      var self = this;
      ImageLibrary.open(this.$el, {
        pickLabel: 'Use as the base product image',
        onPick: function (image) {
          self.ui.modal.find('.f-base-image').val(image.url);
        }
      });
    },

        onChooseProofsheet: function () {
      var self = this;
      ProofsheetProduct.choose(this.ui.modal).done(function (product) {
        self.newProofsheet = product;
        self.ui.modal.find('.f-proofsheet-summary')
          .text(ProofsheetProduct.summary(product.proofsheetProductName, product.proofsheetConfiguration));
        self.ui.modal.find('.mlg-choose-proofsheet').text('Change…');
        self.ui.modal.find('.mlg-clear-proofsheet').show();
      });
    },

    onClearProofsheet: function () {
      this.newProofsheet = null;
      this.ui.modal.find('.f-proofsheet-summary').text('No product: the email has no proofsheet button.');
      this.ui.modal.find('.mlg-choose-proofsheet').text('Choose PIM product…');
      this.ui.modal.find('.mlg-clear-proofsheet').hide();
    },

    /** People paste "cdn.example.com/mug.png"; the backend only fetches http(s). */
    onBaseImageBlur: function (e) {
      var $input = $(e.currentTarget);
      var value = $.trim($input.val());
      if (value && value.indexOf('http') !== 0 && value.indexOf('{{') !== 0) {
        $input.val('https://' + value);
      }
    },

    onCreate: function () {
      var self = this;
      var $modal = this.ui.modal;
      var data = {
        name: $.trim($modal.find('.f-name').val()),
        templateSecId: $modal.find('.f-template').val(),
        segmentSecId: $modal.find('.f-segment').val(),
        baseImageUrl: $.trim($modal.find('.f-base-image').val()),
        productType: $.trim($modal.find('.f-product-type').val()),
        occasion: $modal.find('.f-occasion').val(),
        occasionDetails: $modal.find('.f-occasion-details').val(),
        mandatoryText: $modal.find('.f-mandatory-text').val(),
        // The built-in text unchanged (or empty) is stored as "no override" by the backend.
        imagePrompt: $modal.find('.f-image-prompt').val() || '',
        designLanguage: $modal.find('.f-design-language').val(),
        subject: $.trim($modal.find('.f-subject').val()),
        fromName: $modal.find('.f-from-name').val(),
        fromEmail: $modal.find('.f-from-email').val()
      };
      if (this.newProofsheet) {
        _.extend(data, this.newProofsheet);
      }
      if (!data.name || !data.templateSecId || !data.segmentSecId || !data.baseImageUrl ||
          !data.productType || !data.occasion || !data.subject) {
        Shell.banner(this.$el,
          'Name, template, segment, base image, product type, occasion and subject are required', 'error');
        return;
      }
      var window_ = Shell.readSendWindow($modal);
      if (window_.error) {
        Shell.banner(this.$el, window_.error, 'error');
        return;
      }
      _.extend(data, window_.data);
      var startAt = $modal.find('.f-start-at').val();
      if (startAt) { data.scheduledAt = new Date(startAt).getTime(); }
      MailingApi.createPersonalisedCampaign(data).done(function (campaign) {
        self.closeModal();
        App.navigate('mailing/personalised/' + campaign.secId, { trigger: true });
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    closeModal: function () {
      this.ui.modal.hide().empty();
    },

    //----------Row actions--------------------------------------------------//

    onDuplicate: function (e) {
      var self = this;
      MailingApi.duplicatePersonalisedCampaign(this.secIdOf(e)).done(function (copy) {
        Shell.banner(self.$el, '"' + (copy.name || 'Campaign') + '" created as a draft', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onDelete: function (e) {
      var self = this;
      var name = $(e.currentTarget).closest('tr').find('.c-name a').text();
      if (!window.confirm('Delete "' + name + '"? Its generated images list and statistics are deleted with it.')) {
        return;
      }
      MailingApi.deletePersonalisedCampaign(this.secIdOf(e)).done(function () {
        Shell.banner(self.$el, 'Campaign deleted', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onOpen: function (e) {
      e.preventDefault();
      App.navigate($(e.currentTarget).attr('href').substring(1), { trigger: true });
    },

    secIdOf: function (e) {
      return $(e.currentTarget).closest('tr').data('secid');
    }
  });

  module.exports = PersonalisedView;
});

